import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IStitcherSnapshot } from 'tag/ts/tag/interfaces/snapshots/IStitcherSnapshot';
import { ITagSnapshot } from 'tag/ts/tag/interfaces/snapshots/ITagSnapshot';
import { StitcherSnapshot } from 'tag/ts/tag/utilities/snapshots/StitcherSnapshot';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Tag application snapshot
 */
export class TagSnapshot extends AbstractTagClass implements ITagSnapshot {
	// flag indicating casting state set by desktop skin Model.playbackMode setter
	#casting: boolean = false;

	// stitcher snapshot instance, lazy-initialized
	#stitcherSnapshot: StitcherSnapshot | undefined;

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'TagSnapshot'
			}
		);
	}

	/**
	 * @description Returns autoplay detection results object
	 */
	get autoplay() {
		return this?.tag.controller?.playerManager?.autoplay;
	}

	/**
	 * @description Returns avia player instance
	 */
	get player() {
		return this?.tag?.controller?.playerManager?.player;
	}

	/**
	 * @description Returns current player state from Tag player manager
	 */
	get playerState() {
		return this?.tag?.controller?.playerManager?.player?.playbackState;
	}

	/**
	 * @description Returns currently playing content type from Tag player manager
	 */
	get contentType() {
		return this?.tag?.controller?.playerManager?.contentType;
	}

	/**
	 * @description Fag indicating casting state set by desktop skin Model.playbackMode setter
	 */
	get casting() {
		return this.#casting;
	}

	set casting( value: boolean ) {
		this.#casting = value;
	}

	/**
	 * @description Returns load request provenance history from VideoAPI.
	 * Accessible at `window.SmartTag.list[0].snapshot.loadHistory`.
	 */
	get loadHistory() {
		return this.tag?.controller?.videoAPI?.loadHistory;
	}

	/**
	 * @description Returns stitcher-related snapshot data (session, etc.)
	 * Returns undefined when stitcher params are not configured.
	 */
	get stitcher(): IStitcherSnapshot | undefined {
		// only expose stitcher snapshot when stitcher params are present
		if ( !this.hasStitcherParams() ) {
			return undefined;
		}
		// lazy-initialize stitcher snapshot
		if ( this.utils?.Type?.isEmpty( this.#stitcherSnapshot ) ) {
			this.#stitcherSnapshot = new StitcherSnapshot( { tag: this.tag } );
		}
		return this.#stitcherSnapshot;
	}
}